// errorMessages — shared API-error → user-facing copy map. The
// gateway surfaces backend errors as `{ error: { code, message } }`,
// where `code` is a typed string like ORDER_000102 / LEDGER_000300 /
// MARKET_000400. We render the code's message verbatim when it's
// already trader-friendly, but most service-internal codes come back
// as developer prose ("invalid time_in_force"). The per-code copy
// lives in the `errors` locale namespace (src/locales/<lng>/errors.json,
// SM-286) keyed by the error code itself under `codes.<CODE>`; a code
// with no catalog entry falls through to the message-parsing fallback
// below, exactly as the old in-file map did.
//
// Notable code-specific context (copy now in the catalog):
//   - ORDER_000128: sell quantity exceeds position minus open SELL
//     reservations — the server message is "insufficient available
//     tickets".
//   - CLIENT_0007xx (MFA): we surface friendly copy instead of the raw
//     server publicMessage ("mfa response invalid", "mfa challenge
//     expired", etc).
import { APIError } from "./api";
import i18next from "@/lib/i18n";

// Transport / gateway failures that arrive WITHOUT our typed { error }
// envelope: an ingress 504 (or a rollout drain-gap 502) is an HTML page,
// not JSON, so it reaches us as code "UNKNOWN" with a bare "HTTP 504"
// message. The `http.<status>` catalog entries map the status to plain
// copy so we never show a trader a raw status number. These are usually
// transient, so the copy invites a retry.

/**
 * Render an error from any API call into trader-friendly copy.
 * Falls back to the underlying message when no map entry exists, then
 * to a generic "Something went wrong".
 */
export function humanizeError(err: unknown): string {
  if (err instanceof APIError) {
    const codeKey = `errors:codes.${err.code}`;
    if (i18next.exists(codeKey)) return i18next.t(codeKey);
    // Server / gateway class (timeout, rate-limit, 5xx) carries no typed
    // code — speak to the status before falling back to message parsing.
    const statusKey = `errors:http.${err.status}`;
    if (i18next.exists(statusKey)) return i18next.t(statusKey);
    if (err.status >= 500) return i18next.t("errors:fallback.server");
    // Strip the "CODE: code: " noise that Go's wrapped errors leave
    // ("ORDER_000116: reservation failed (insufficient credit or
    // shares): ORDER_000116: reservation failed …") — the second
    // colon-segment is usually the real human prose. Also drop a bare
    // "HTTP nnn" so an unmapped status never leaks the number.
    const parts = err.message
      .split(/[:;]/)
      .map((s) => s.trim())
      .filter((s) => s && !/^[A-Z]+_\d+$/.test(s) && !/^HTTP \d+$/i.test(s));
    if (parts.length > 0) return parts[parts.length - 1];
    return i18next.t("errors:fallback.generic");
  }
  // A request that never got a response (offline, DNS, CORS, connection
  // dropped) rejects with a TypeError, not an APIError.
  if (err instanceof Error) {
    if (
      /failed to fetch|networkerror|network request failed|load failed/i.test(
        err.message
      )
    )
      return i18next.t("errors:fallback.network");
    return err.message;
  }
  return i18next.t("errors:fallback.generic");
}

/**
 * MFA-aware variant of humanizeError for the login second-factor step.
 *
 * A wrong second factor always comes back as the same code regardless of
 * the factor kind (CLIENT_000713 "incorrect mfa response", or
 * CLIENT_000714 "mfa response invalid" for a malformed/empty entry), so
 * the *method* — known only on the client from the selected factor — is
 * what makes the copy specific. For that "didn't match / invalid" family
 * we render per-method copy; everything else (expired challenge, locked
 * out, factor not found, …) is method-agnostic and falls through to the
 * shared humanizeError map.
 */
/**
 * Change-password variant of humanizeError.
 *
 * A wrong *current* password comes back as the same anti-leak code the
 * login path uses (CLIENT_000204 "invalid email or password"). On the
 * change-password screen there's no email field, so that copy is both
 * confusing and wrong — the only thing the user could have gotten wrong
 * is the current password. Rewrite just that case; everything else
 * (weak new password, server errors, …) falls through unchanged.
 */
export function humanizePasswordError(err: unknown): string {
  if (
    err instanceof APIError &&
    (err.code === "CLIENT_000204" || err.code === "GATEWAY_000023")
  ) {
    return i18next.t("errors:password.currentIncorrect");
  }
  return humanizeError(err);
}

/**
 * Crypto-wallet variant of humanizeError (SM-212).
 *
 * crypto-wallet-service CWALLET_* codes carry messages written for the
 * end user ("Amount is below the minimum withdrawal", "Withdrawals are
 * paused", …), so we surface them VERBATIM instead of routing through the
 * generic colon-stripping fallback, which could truncate them. Everything
 * else falls through to the shared map.
 */
export function humanizeCryptoError(err: unknown): string {
  if (err instanceof APIError && err.code.startsWith("CWALLET_") && err.message) {
    return err.message;
  }
  return humanizeError(err);
}

export function humanizeMFAError(err: unknown, kind: string): string {
  if (
    err instanceof APIError &&
    (err.code === "CLIENT_000713" || err.code === "CLIENT_000714")
  ) {
    switch (kind.toUpperCase()) {
      case "PASSPHRASE":
        return i18next.t("errors:mfa.PASSPHRASE");
      case "BACKUP_CODE":
        return i18next.t("errors:mfa.BACKUP_CODE");
      case "EMAIL_OTP":
        return i18next.t("errors:mfa.EMAIL_OTP");
      case "TOTP":
      default:
        return i18next.t("errors:mfa.TOTP");
    }
  }
  return humanizeError(err);
}
